/**
 * Per-placement instance writes — the contract that decides whether a
 * placement's loaded file may share mesh state with other placements of the
 * same file.
 *
 * The container cache instantiates a file once per placement, and a Babylon
 * instance shares its source's geometry and MorphTargetManager. That is free
 * while nothing writes to them, and wrong the moment something does per
 * placement: two avatars of one file would make the same face.
 *
 * Whatever writes into a placement's instance registers a claim here; the
 * loader asks {@link writesInstancePerPlacement} and clones instead of
 * instancing when any claim holds. Core knows the question, not who asks it
 * (DESIGN_PRINCIPLES §10.11) — so a new writer registers, it does not add a
 * clause to the model loader.
 */

import type { SceneObject } from './SceneObject';

/** Answers true when `object` will write its instance's mesh state per placement. */
export type InstanceWriteClaim = (object: SceneObject) => boolean;

const claims = new Map<string, InstanceWriteClaim>();

/**
 * Register a claim under a name (re-registering the name replaces it, so a
 * hot-reloaded module does not stack copies). Returns the unregisterer.
 */
export function registerInstanceWriteClaim(name: string, claim: InstanceWriteClaim): () => void {
    claims.set(name, claim);
    return () => {
        if (claims.get(name) === claim) claims.delete(name);
    };
}

/** Whether any registered claim says `object` writes its instance per placement. */
export function writesInstancePerPlacement(object: SceneObject): boolean {
    for (const claim of claims.values()) {
        if (claim(object)) return true;
    }
    return false;
}
